import {
    type FC,
    type RefObject,
    useEffect,
    useRef,
    type MouseEvent,
    type ReactElement,
} from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { PdpRespSimilarItemCarousel } from './PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { viewSimilarItems, ItemsSimilarToHeader } from '../pdpMessages';
import { PdpSharedUnpurchasableHeader } from '../PdpSharedUnpurchasableIcons/PdpSharedUnpurchasableHeader';
import { PdpRespSponsoredRelatedItemsSubtitle } from './PdpRespSponsoredRelatedItemsSubtitle';

import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { usePdpRespMarketplaceLayoutPrimaryContext } from '../respMarketplace/PdpRespMarketplaceLayout/PdpRespMarketplaceLayoutPrimaryContextProvider';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useMergedSponsoredAndYmalItems } from './hooks/useMergedSponsoredAndYmalItems';
import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';

import { locations } from '../../utils/tracking/shared/favoritesTracking';

// helpers
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import {
    trackItemClick,
    trackEcommerceLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
    trackItemsDisplayed,
    trackLandingItemSponsoredImpressions,
    trackPdpTopYmalModuleImpression,
} from '../tracking/similarItemsTracking';
import { trackSponsoredItemsEvent } from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { trackPdpYmalRerankTest } from '../../utils/abTestHelper';
import {
    PDP_TOP_SIMILAR_ITEMS_COUNT,
    PDP_TOP_SIMILAR_ITEMS_COUNT_VARIANT,
} from '../../constants/pdpConstants';

import { type PdpRespSimilarItemsLanding_sponsoredItems$key } from './__generated__/PdpRespSimilarItemsLanding_sponsoredItems.graphql';
import { type PdpRespSimilarItemsLanding_item$key } from './__generated__/PdpRespSimilarItemsLanding_item.graphql';
import { type PdpRespSimilarItemsLanding_similarItems$key } from './__generated__/PdpRespSimilarItemsLanding_similarItems.graphql';
import { type PdpRespSimilarItemsLanding_user$key } from './__generated__/PdpRespSimilarItemsLanding_user.graphql';
import { type PdpRespSimilarItemsLanding_similarItem$key } from './__generated__/PdpRespSimilarItemsLanding_similarItem.graphql';

// styles
import styles from './PdpRespSimilarItemsLanding.scss';

// types
type PdpRespSimilarItemsLandingComponentProps = {
    sponsoredItems: PdpRespSimilarItemsLanding_sponsoredItems$key | null | undefined;
    item: PdpRespSimilarItemsLanding_item$key;
    similarItems: PdpRespSimilarItemsLanding_similarItems$key | null | undefined;
    user: PdpRespSimilarItemsLanding_user$key | null | undefined;
    scrollToRef: RefObject<HTMLElement>;
    landingItemSize?: string;
    currency: string;
};

// GraphQL Fragments
const sponsoredItemsFragment = graphql`
    fragment PdpRespSimilarItemsLanding_sponsoredItems on SponsoredItems {
        ...useMergedSponsoredAndYmalItems_sponsoredItems
    }
`;

const userFragment = graphql`
    fragment PdpRespSimilarItemsLanding_user on User {
        serviceId
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarItemsLanding_item on Item {
        title
        isSold
        isListingNotListed
        isListingUnlisted
        seller {
            isInternal
            isAccessible
        }
        ...PdpSharedUnpurchasableHeader_item
        ...PdpRespSimilarItemsContainer_item
        ...PdpRespSimilarItemCarousel_baseItem
    }
`;

const similarItemsFragment = graphql`
    fragment PdpRespSimilarItemsLanding_similarItems on moreLikeThisItemConnection {
        ...useMergedSponsoredAndYmalItems_similarItems
    }
`;

const mergedItemsFragment = graphql`
    fragment PdpRespSimilarItemsLanding_similarItem on Item @relay(plural: true) {
        serviceId
        categoryPath
        categoryCode
        browseUrl
        title
        ecommerceTrackingParams
        ...PdpRespSimilarItemCarousel_item
        ...FavoritesProvider_item
    }
`;

export const PdpRespSimilarItemsLanding: FC<PdpRespSimilarItemsLandingComponentProps> = ({
    sponsoredItems: sponsoredItemsRef,
    item: itemRef,
    similarItems: similarItemsRef,
    user: userRef,
    scrollToRef,
    landingItemSize,
    currency,
}) => {
    const sponsoredItems = useFragment(sponsoredItemsFragment, sponsoredItemsRef);
    const item = useFragment(itemFragment, itemRef);
    const similarItems = useFragment(similarItemsFragment, similarItemsRef);
    const user = useFragment(userFragment, userRef);

    const sectionVisibilityRef = useRef(null);
    const isClient = useClientState();
    const hasFiredImpressions = useRef(false);
    const { relayVariables } = usePdpRespMarketplaceLayoutPrimaryContext();
    const { pdpTopYmalSponsoredTestVariant } = useServerVarsContext();

    const handleSectionVisibilityChange = ({
        isVisible,
    }: Pick<VisibilityChangeCallback, 'isVisible'>): void => {
        if (isVisible) {
            trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS' });
            trackPdpYmalRerankTest();
        }
    };

    const scrollToPdpRespSimilarItemsGrid = (): void => {
        const gridEl = scrollToRef?.current;
        if (gridEl) {
            scrollIntoView(gridEl, { time: 500 }, () => gridEl.focus());
        }
    };

    const afterDisplay = !!relayVariables.variables.afterDisplay;
    const { isListingNotListed, isListingUnlisted, isSold, seller } = item || {};
    const { isAccessible: isSellerAccessible, isInternal: isSellerInternal } = seller || {};

    const isItemListingNotListed = isListingNotListed && !isSellerInternal;
    const isPdpTypeDisplayUnpurchasable =
        ((isListingUnlisted || isSold) && !isItemListingNotListed) || !isSellerAccessible;

    const isPdpTopYmalSponsoredTestVariant = pdpTopYmalSponsoredTestVariant === 'variant';
    const purchasableItemCount = isPdpTopYmalSponsoredTestVariant
        ? PDP_TOP_SIMILAR_ITEMS_COUNT_VARIANT
        : PDP_TOP_SIMILAR_ITEMS_COUNT;

    const { mergedItems, sponsoredMetadataIndexMap } = useMergedSponsoredAndYmalItems({
        similarItems,
        sponsoredItems,
        isPdpTypeDisplayUnpurchasable,
        isPdpTopYmalSponsoredTestVariant,
        purchasableItemCount,
    });

    const landingItems = useFragment<PdpRespSimilarItemsLanding_similarItem$key>(
        mergedItemsFragment,
        mergedItems
    );

    const onClickItem = (index: number, event: MouseEvent | null = null): void => {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS' }, event as $TSFixMe);
        trackEcommerceLandingItemClick({
            index,
            landingItem: {
                node: { ...landingItems[index], isSponsored: !!sponsoredMetadataIndexMap?.[index] },
            },
            isSoldPdp: isPdpTypeDisplayUnpurchasable,
        });

        const trackingLink = sponsoredMetadataIndexMap?.[index]?.clickTrackerLink;
        if (trackingLink) {
            trackSponsoredItemsEvent(trackingLink);
        }
    };

    const onClickViewMore = (event?: MouseEvent | null): void => {
        scrollToPdpRespSimilarItemsGrid();
        // fire tracking
        trackLandingViewAllClick(event);
    };

    const ecommerceTrackingParams = landingItems[0]?.ecommerceTrackingParams;
    useEffect(() => {
        if (
            !isPdpTopYmalSponsoredTestVariant &&
            !hasFiredImpressions.current &&
            ecommerceTrackingParams
        ) {
            if (landingItems.length) {
                trackLandingItemImpressions(
                    landingItems.map((landingItem, index) => ({
                        node: { ...landingItem, isSponsored: !!sponsoredMetadataIndexMap?.[index] },
                    })),
                    isPdpTypeDisplayUnpurchasable
                );
                hasFiredImpressions.current = true;
            }
        }
    }, [
        hasFiredImpressions,
        ecommerceTrackingParams,
        landingItems,
        isPdpTypeDisplayUnpurchasable,
        isPdpTopYmalSponsoredTestVariant,
        item,
        sponsoredMetadataIndexMap,
    ]);

    let initialLandingItems = landingItems;
    let additionalLandingItems: typeof landingItems | undefined;

    let header: ReactElement | undefined = undefined;
    let subtitle: ReactElement;
    let isHorizontal = false;

    if (initialLandingItems?.length === 0) {
        return null;
    }

    if (isPdpTypeDisplayUnpurchasable) {
        // Unpurchasable Pdp
        header = <PdpSharedUnpurchasableHeader item={item} />;
        subtitle = viewSimilarItems;
    } else if (isPdpTopYmalSponsoredTestVariant) {
        subtitle = <PdpRespSponsoredRelatedItemsSubtitle />;
        isHorizontal = true;
    } else {
        // GROWTH-1429: Test Similar Items on Pdp
        subtitle = <ItemsSimilarToHeader title={<i>{item.title}</i>} />;
        isHorizontal = true;
    }

    if (initialLandingItems.length > purchasableItemCount) {
        additionalLandingItems = initialLandingItems.slice(purchasableItemCount);
        initialLandingItems = initialLandingItems.slice(0, purchasableItemCount);
    }

    const landingItemsClassNames = classnames(styles.landingItems, 'rowFlex', {
        [styles.isHorizontal]: isHorizontal,
    });

    const landingItemClassNames = classnames('colLg2', 'colMd4', 'colXs6', styles.landingItem, {
        [styles.smallLandingItem]: landingItemSize === 'small',
        [styles.sevenItems]: isPdpTopYmalSponsoredTestVariant,
    });

    return (
        <div ref={sectionVisibilityRef}>
            <VisibilityTracker
                elementRef={sectionVisibilityRef}
                onVisibilityChange={handleSectionVisibilityChange}
            />
            {afterDisplay && (
                <VisibilityTracker
                    elementRef={sectionVisibilityRef}
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            if (pdpTopYmalSponsoredTestVariant) {
                                trackPdpTopYmalModuleImpression(pdpTopYmalSponsoredTestVariant);
                            }
                            if (isPdpTopYmalSponsoredTestVariant) {
                                trackLandingItemImpressions(
                                    landingItems.map((landingItem, index) => ({
                                        node: {
                                            ...landingItem,
                                            isSponsored: !!sponsoredMetadataIndexMap?.[index],
                                        },
                                    })),
                                    isPdpTypeDisplayUnpurchasable
                                );
                                trackLandingItemSponsoredImpressions(
                                    landingItems,
                                    sponsoredMetadataIndexMap
                                );
                            }
                        }
                    }}
                />
            )}
            <FavoritesProvider
                location={locations.SIMILAR_ITEM_TOP}
                disable={!isClient}
                userId={user?.serviceId || null}
                items={landingItems}
                fetchFolder={false}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <PdpRespSimilarItemsContainer
                        isDark
                        isTopContainer
                        header={header}
                        subtitle={subtitle}
                        onClickViewMore={onClickViewMore}
                        dataTn="pdp-similar-landing-grid"
                        isHorizontal={isHorizontal}
                        item={item}
                    >
                        {/* Grid of Items */}
                        {!initialLandingItems.length ? null : (
                            <>
                                <div className={landingItemsClassNames}>
                                    {initialLandingItems.map((landingItem, index) => (
                                        <div
                                            key={landingItem?.serviceId}
                                            className={landingItemClassNames}
                                        >
                                            <PdpRespSimilarItemCarousel
                                                baseItem={item}
                                                item={landingItem}
                                                index={index}
                                                favorites={favorites}
                                                onClick={onClickItem}
                                                isCondensed={isHorizontal}
                                                currency={currency}
                                                isLoading={
                                                    isPdpTopYmalSponsoredTestVariant &&
                                                    !afterDisplay
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                                {additionalLandingItems ? (
                                    <div className={landingItemsClassNames}>
                                        {additionalLandingItems.map((landingItem, index) => (
                                            <div
                                                key={landingItem?.serviceId}
                                                className={landingItemClassNames}
                                            >
                                                <PdpRespSimilarItemCarousel
                                                    baseItem={item}
                                                    item={landingItem}
                                                    index={index + purchasableItemCount}
                                                    favorites={favorites}
                                                    onClick={onClickItem}
                                                    isCondensed={isHorizontal}
                                                    currency={currency}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </>
                        )}
                    </PdpRespSimilarItemsContainer>
                )}
            </FavoritesProvider>
        </div>
    );
};
